/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  forwardRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ICAutocompleteGenericModel } from "./models/bfx-interface-controls.models";
import { ICIBase } from "./interface/ic-base.interface";

import { ConstraintValue } from "shared/cubiq-services/model/core/ConstraintValue";

export const ALLOW_PARTIAL_FILL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICAllowPartialFillsComponent),
  multi: true,
};

const LABEL = "Allow Partial Fills";

class ComponentData {
  private _value: ConstraintValue = ConstraintValue.None;

  label: string;
  locked: boolean;

  checkbox: {
    value: boolean;
  };

  constructor(private component: ICAllowPartialFillsComponent) {
    const context = this;
    this.label = LABEL;
    this.locked = false;
    this.checkbox = Object.defineProperties(
      {},
      {
        value: {
          get: () => ConstraintValue.isEnabled(this._value),
          set: (checkboxValue: boolean) => {
            context.value = checkboxValue ? ConstraintValue.Enabled : ConstraintValue.Disabled;
          },
        },
      },
    ) as {
      value: boolean;
    };
  }

  /**
   * Use when template binding from writeValue
   */
  resetValue() {
    this._value = ConstraintValue.None;
  }

  get value() {
    return this._value;
  }

  /**
   * do not call from writeValue
   * !ConstraintValue.is(value)
   */
  set value(value: ConstraintValue) {
    if (!ConstraintValue.is(value)) value = ConstraintValue.None;

    // important to ignore value None to None due to null initialization of template
    if (this._value !== value) {
      this._value = value;
      this.component.onModelChange(this._value);
      this.component.cd.markForCheck();
    }
  }
}

@Component({
  selector: "ic-allow-partial-fills",
  templateUrl: "./html/ic-allow-partial-fills.html",
  providers: [ALLOW_PARTIAL_FILL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICAllowPartialFillsComponent
  implements OnInit, OnDestroy, ControlValueAccessor, ICIBase
{
  /**********************************************************************/
  /* INPUT */

  @Input() inputId: string;

  private _input_client: CubiqTS.ClientImpl;

  public get input_client(): CubiqTS.ClientImpl {
    return this._input_client;
  }

  @Input("client")
  public set input_client(value: CubiqTS.ClientImpl) {
    this.last_client = this._input_client;

    this._input_client = value;

    if (
      this.last_client?.clientId !== this._input_client?.clientId &&
      this.isValidClient(this._input_client)
    ) {
      let v: ConstraintValue = this._input_client.allowPartialFillFg
        ? ConstraintValue.FlagEnabled
        : ConstraintValue.FlagDisabled;
      if (!this.isPartFillEligible(this._input_orderType?.value)) {
        v = ConstraintValue.asInvalid(v);
      }
      this.data.value = v;
    }
  }

  private _input_orderType: ICAutocompleteGenericModel<CubiqTS.RequestOrderType>;
  public get input_orderType(): ICAutocompleteGenericModel<CubiqTS.RequestOrderType> {
    return this._input_orderType;
  }

  @Input("orderType")
  public set input_orderType(value: ICAutocompleteGenericModel<CubiqTS.RequestOrderType>) {
    this.last_orderType = this._input_orderType?.value ?? null;
    this._input_orderType = value;

    if (this.last_orderType != value?.value) {
      if (this.isPartFillEligible(value?.value)) {
        if (ConstraintValue.isInvalid(this.data.value)) {
          this.data.value = ConstraintValue.asValid(this.data.value);
        }
      } else {
        this.data.value = ConstraintValue.asInvalid(this.data.value);
      }
    }
  }

  private last_client: CubiqTS.ClientImpl;
  private last_orderType: CubiqTS.RequestOrderType;

  @Input("label") input_label: any = "Allow Partial Fills";

  @Input("loading") isLoading: boolean = true;

  public onModelChange: Function = (value: any) => {};
  public onModelTouched: Function = () => {};

  /**********************************************************************/
  /* DATA */

  public data: ComponentData;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(public cd: ChangeDetectorRef) {
    this.data = this.initializeData();
  }

  private initializeData(): ComponentData {
    const data: ComponentData = new ComponentData(this);
    return data;
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */
  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  /**********************************************************************/
  /* INITIALIZATION */
  public initialize(): void {
    this.data.label = this.input_label;
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  public writeValue(value: any): void {
    if (!ConstraintValue.is(value) || value == null) {
      this.data.resetValue();
    } else {
      this.data.value = value;
    }
  }

  public registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  public registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* BASIC - API */

  public load(data: {
    client: CubiqTS.ClientImpl;
    orderType: ICAutocompleteGenericModel<CubiqTS.RequestOrderType>;
    value: ConstraintValue;
  }): void {
    if (this._input_orderType?.value !== data.orderType?.value) {
      this._input_orderType = data.orderType;
    }

    if (data.value == null) {
      this.input_client = data.client;
    } else if (this.data.value !== data.value) {
      if (this._input_client !== data.client) {
        this._input_client = data.client;
      }

      this.data.value = data.value;
    }
    this.cd.markForCheck();
  }

  public reset(): void {
    if (ConstraintValue.None !== this.data.value || this.data.locked !== false) {
      this.data.value = ConstraintValue.None;
      this.data.locked = false;
      this.cd.markForCheck();
    }
  }

  public lock(): void {
    if (this.data.locked !== true) {
      this.data.locked = true;
      this.cd.markForCheck();
    }
  }

  public unlock(): void {
    if (this.data.locked !== false) {
      this.data.locked = false;
      this.cd.markForCheck();
    }
  }

  private isValidClient(client: CubiqTS.ClientImpl) {
    return client != null && client instanceof CubiqTS.ClientImpl;
  }

  private isPartFillEligible(value: unknown) {
    return value === CubiqTS.RequestOrderType.TP || value === CubiqTS.RequestOrderType.MARKET;
  }

  public onFocus(event: any): void {}

  public onBlur(event: any): void {}

  public set(value: any): void {}

  public validate(): boolean {
    return true;
  }
}
